import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { Campaign, CreateCampaignRequest, CampaignFilters, CampaignCurrency, SelectedCampaignProfile } from '../../../shared/types/campaign';
import { RateCard } from '../../../shared/types/rateCard';

interface CampaignFormProps {
  campaign?: Campaign;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (campaignData: CreateCampaignRequest) => Promise<void>;
  loading?: boolean;
  // Emit changes as the user edits to enable live preview
  onChange?: (campaignData: CreateCampaignRequest) => void;
  // If true, render inline instead of modal overlay
  inline?: boolean;
  // Rate cards for selection
  rateCards?: RateCard[];
}

const CampaignForm: React.FC<CampaignFormProps> = ({
  campaign,
  isOpen,
  onClose,
  onSubmit,
  loading = false,
  onChange,
  inline = false,
  rateCards = [],
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    shortUrl: '',
    currency: undefined as CampaignCurrency | undefined,
    rateCardId: undefined as string | undefined,
    selectedProfiles: [] as SelectedCampaignProfile[],
    isActive: true,
    profileFilters: {
      roles: [] as string[],
      skills: [] as string[],
      rolesSearch: '',
      skillsSearch: '',
    } as CampaignFilters,
  });

  const [newRole, setNewRole] = useState('');
  const [newSkill, setNewSkill] = useState('');

  useEffect(() => {
    if (campaign) {
      setFormData({
        title: campaign.title,
        description: campaign.description,
        shortUrl: campaign.shortUrl || '',
        currency: campaign.currency,
        rateCardId: campaign.rateCardId,
        selectedProfiles: campaign.selectedProfiles || [],
        isActive: campaign.isActive,
        profileFilters: {
          roles: campaign.profileFilters.roles || [],
          skills: campaign.profileFilters.skills || [],
          rolesSearch: campaign.profileFilters.rolesSearch || '',
          skillsSearch: campaign.profileFilters.skillsSearch || '',
        },
      });
    } else {
      setFormData({
        title: '',
        description: '',
        shortUrl: '',
        currency: undefined,
        rateCardId: undefined,
        selectedProfiles: [],
        isActive: true,
        profileFilters: {
          roles: [],
          skills: [],
          rolesSearch: '',
          skillsSearch: '',
        },
      });
    }
  }, [campaign, isOpen]);

  // Notify parent on every change for live preview
  useEffect(() => {
    if (onChange) {
      onChange(formData);
    }
  }, [formData, onChange]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(formData);
  };

  const addRole = () => {
    if (newRole.trim() && !formData.profileFilters.roles?.includes(newRole.trim())) {
      setFormData(prev => ({
        ...prev,
        profileFilters: {
          ...prev.profileFilters,
          roles: [...prev.profileFilters.roles!, newRole.trim()],
        },
      }));
      setNewRole('');
    }
  };

  const removeRole = (roleToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      profileFilters: {
        ...prev.profileFilters,
        roles: prev.profileFilters.roles?.filter(role => role !== roleToRemove),
      },
    }));
  };

  const addSkill = () => {
    if (newSkill.trim() && !formData.profileFilters.skills?.includes(newSkill.trim())) {
      setFormData(prev => ({
        ...prev,
        profileFilters: {
          ...prev.profileFilters,
          skills: [...prev.profileFilters.skills!, newSkill.trim()],
        },
      }));
      setNewSkill('');
    }
  };

  const removeSkill = (skillToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      profileFilters: {
        ...prev.profileFilters,
        skills: prev.profileFilters.skills?.filter(skill => skill !== skillToRemove),
      },
    }));
  };

  if (!isOpen) return null;

  // Inline rendering (embedded in page)
  if (inline) {
    return (
      <div className="bg-white rounded-lg shadow border border-gray-200 w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {campaign ? 'Edit Campaign' : 'Create New Campaign'}
          </h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Campaign Title *
            </label>
            <input
              type="text"
              id="title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label htmlFor="shortUrl" className="block text-sm font-medium text-gray-700 mb-2">
              Short URL
            </label>
            <input
              type="text"
              id="shortUrl"
              value={formData.shortUrl}
              onChange={(e) => setFormData(prev => ({ ...prev, shortUrl: e.target.value }))}
              placeholder="e.g., summer-campaign-2025"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Used to generate a friendly share link.</p>
          </div>

          {/* Description removed per requirements */}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Currency
            </label>
            <select
              value={formData.currency || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, currency: (e.target.value || undefined) as CampaignCurrency }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select currency</option>
              <option value="USD">USD</option>
              <option value="GBP">GBP</option>
              <option value="EUR">EUR</option>
              <option value="BRL">BRL</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rate Card
            </label>
            <select
              value={formData.rateCardId || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, rateCardId: e.target.value || undefined }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select Rate Card</option>
              {rateCards.map((rateCard) => (
                <option key={rateCard.id} value={rateCard.id}>
                  {rateCard.title} ({rateCard.roles.length} roles)
                </option>
              ))}
            </select>
          </div>

          {/* Profile Filters hidden for now */}

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
              Active campaign
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Saving...' : (campaign ? 'Update Campaign' : 'Create Campaign')}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 w-full">
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900">
          {campaign ? 'Edit Campaign' : 'Create New Campaign'}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
            Campaign Title *
          </label>
          <input
            type="text"
            id="title"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
            Description *
          </label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>


        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Profile Filters
          </label>

          {/* Roles */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-600 mb-2">Roles</label>
            <div className="flex space-x-2 mb-2">
              <input
                type="text"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                placeholder="Add a role (e.g., Senior Developer)"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addRole())}
              />
              <button
                type="button"
                onClick={addRole}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.profileFilters.roles?.map((role) => (
                <span
                  key={role}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                >
                  {role}
                  <button
                    type="button"
                    onClick={() => removeRole(role)}
                    className="ml-2 text-blue-600 hover:text-blue-800"
                  >
                    <FontAwesomeIcon icon={faTimes} className="text-xs" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Skills */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-600 mb-2">Skills</label>
            <div className="flex space-x-2 mb-2">
              <input
                type="text"
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                placeholder="Add a skill (e.g., React, C#)"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
              />
              <button
                type="button"
                onClick={addSkill}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.profileFilters.skills?.map((skill) => (
                <span
                  key={skill}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800"
                >
                  {skill}
                  <button
                    type="button"
                    onClick={() => removeSkill(skill)}
                    className="ml-2 text-green-600 hover:text-green-800"
                  >
                    <FontAwesomeIcon icon={faTimes} className="text-xs" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Role Search */}
          <div className="mb-4">
            <label htmlFor="rolesSearch" className="block text-sm font-medium text-gray-600 mb-2">
              Role Search Term
            </label>
            <input
              type="text"
              id="rolesSearch"
              value={formData.profileFilters.rolesSearch}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                profileFilters: { ...prev.profileFilters, rolesSearch: e.target.value }
              }))}
              placeholder="Search for roles containing this text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Skill Search */}
          <div className="mb-4">
            <label htmlFor="skillsSearch" className="block text-sm font-medium text-gray-600 mb-2">
              Skill Search Term
            </label>
            <input
              type="text"
              id="skillsSearch"
              value={formData.profileFilters.skillsSearch}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                profileFilters: { ...prev.profileFilters, skillsSearch: e.target.value }
              }))}
              placeholder="Search for skills containing this text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="isActive"
            checked={formData.isActive}
            onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
            Active campaign
          </label>
        </div>

        {formData.selectedProfiles && formData.selectedProfiles.length > 0 && (
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-md font-semibold text-gray-900 mb-3">Selected Profiles</h4>
            <div className="space-y-3">
              {formData.selectedProfiles.map(sp => {
                const selectedRateCard = rateCards.find(rc => rc.id === formData.rateCardId);
                return (
                  <div key={sp.profileId} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div className="flex-1">
                      <div className="text-sm text-gray-700 font-medium">{sp.profileId}</div>
                      <div className="text-xs text-gray-500">Select a role from the rate card</div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <select
                        value={sp.selectedRoleId || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          selectedProfiles: prev.selectedProfiles.map(x =>
                            x.profileId === sp.profileId
                              ? { ...x, selectedRoleId: e.target.value }
                              : x
                          )
                        }))}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      >
                        <option value="">Select Role</option>
                        {selectedRateCard?.roles.map(role => (
                          <option key={role.id} value={role.id}>
                            {role.title} - {formData.currency || 'USD'} {role.dailyRate}/day
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Saving...' : (campaign ? 'Update Campaign' : 'Create Campaign')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CampaignForm;
